import axios from 'axios';
import { useAuthStore } from '@/store/useAuthStore';
import { getActiveTreeId, useTreeContextStore } from '@/store/useTreeContextStore';

// ─── Typisierter API-Fehler ──────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Axios-Instanz ──────────────────────────────────────────────────────────

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  withCredentials: true, // Cookies mitsenden (Refresh-Token)
});

// ─── Request-Interceptor ────────────────────────────────────────────────────

apiClient.interceptors.request.use((config) => {
  // Content-Type nur setzen, wenn nicht bereits explizit gesetzt (z.B. multipart/form-data).
  //
  // Bei FormData gar nicht anfassen: der Header braucht die von Axios erzeugte
  // boundary. Setzt man hier application/json, geht der Upload als JSON raus,
  // der Server findet keine Datei – und die Fehlermeldung („Keine Datei
  // hochgeladen") zeigt in die falsche Richtung.
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (!isFormData && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  const { accessToken, csrfToken } = useAuthStore.getState();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Aktiver Stammbaum (Mandant). Wird immer mitgesendet, sobald einer gewählt
  // ist – globale Routen (Auth, Nutzer, Rollen, Backup, Admin) ignorieren ihn.
  const treeId = getActiveTreeId();
  if (treeId) {
    config.headers['X-Tree-Id'] = treeId;
  }

  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }

  return config;
});

// ─── Response-Interceptor (mit 401-Refresh) ─────────────────────────────────

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  for (const { resolve, reject } of refreshQueue) {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  }
  refreshQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error)) throw error;

    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    const status = error.response?.status ?? 500;
    const errorCode = (error.response?.data as { error?: string })?.error;

    // Bei 401 versuchen wir einen Token-Refresh – außer für die wenigen
    // UNauthentifizierten Auth-Endpunkte (sonst Endlosschleife bzw. sinnloser
    // Refresh). Authentifizierte /auth/-Endpunkte (2FA-/Passkey-Verwaltung,
    // Passwort ändern) müssen den Refresh durchlaufen, sonst schlagen sie nach
    // Ablauf des 15-Minuten-Access-Tokens grundlos fehl.
    const url = originalRequest.url ?? '';
    const noRefreshEndpoints = [
      '/auth/login',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/accept-invite',
    ];
    const skipRefresh = noRefreshEndpoints.some((p) => url.includes(p));

    // Auch bei einem CSRF-Fehler (403 CSRF_INVALID) einmalig neu
    // authentifizieren: /auth/refresh rotiert csrf_token-Cookie UND Store-Token
    // synchron, sodass ein Token-Desync (z. B. nach längerer Inaktivität oder
    // parallelen Requests) automatisch behoben wird statt den Nutzer scheitern
    // zu lassen. Der _retry-Guard verhindert Endlosschleifen.
    const isCsrfFailure = status === 403 && errorCode === 'CSRF_INVALID';

    if ((status === 401 || isCsrfFailure) && !originalRequest._retry && !skipRefresh) {
      if (isRefreshing) {
        // Warte bis der laufende Refresh fertig ist
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              originalRequest._retry = true;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        // Der aktive Stammbaum MUSS mit. Die Rechte gelten je Baum, und
        // /auth/refresh liefert ohne diesen Kopf eine leere Rechteliste
        // zurück – die dann unten im Store landet. Wirkung: nach einem
        // einzigen 401 irgendwo in der Anwendung hielt sich jeder Nicht-Admin
        // für rechtlos. Knöpfe verschwanden, Seiten meldeten „keine
        // Berechtigung", und ein Neuladen half, bis der nächste 401 kam.
        //
        // Gemessen: Store 22 Rechte → Anfrage mit abgelaufenem Token → Store 0.
        // Admins merkten davon nichts, weil `hasPermission` bei ihnen über die
        // Rolle abkürzt. Deshalb fiel es so lange niemandem auf.
        //
        // Hier mit blankem axios statt über apiClient, damit ein 401 auf
        // /auth/refresh nicht wieder in diesem Abfangjäger landet.
        const treeId = getActiveTreeId();
        const { data } = await axios.post(
          '/api/auth/refresh',
          {},
          {
            withCredentials: true,
            headers: treeId ? { 'X-Tree-Id': treeId } : undefined,
          },
        );
        const { accessToken, csrfToken, user } = data;
        useAuthStore.getState().setAuth(accessToken, user, csrfToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        processQueue(null, accessToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    // Aktiver Stammbaum ungültig geworden (gelöscht oder Zugriff entzogen):
    // gemerkte Auswahl verwerfen, damit der AuthGuard neu auswählen lässt.
    if (
      (status === 403 && errorCode === 'TREE_FORBIDDEN') ||
      (status === 404 && errorCode === 'TREE_NOT_FOUND')
    ) {
      useTreeContextStore.getState().clearActiveTree();
    }

    // Standard-Fehlerbehandlung
    const message =
      (error.response?.data as { message?: string })?.message ??
      error.message ??
      'Ein unbekannter Fehler ist aufgetreten.';

    throw new ApiError(message, status, errorCode, error.response?.data);
  },
);

export default apiClient;
